import {
  boundaryEditProblem,
  buildBoundaryEditPatch,
  buildNewZonePatch,
  isBoundaryEditDirty,
  newZoneFromEdit,
  UpdateFarm,
} from "portal/utils/boundaryEdit";
import {
  Button,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { CenterPivot } from "./CenterPivot";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { DeepPartial } from "portal/utils/objects";
import { EditablePoint } from "./EditablePoint";
import {
  endBoundaryEdit,
  setFocus,
  setSelection,
} from "portal/state/farmExplorer";
import { Field } from "./Field";
import { NEW_ZONE_TYPE_I18N_KEYS, NewZoneDetails } from "./NewZoneDetails";
import { Obstacle } from "./Obstacle";
import { Path } from "./Path";
import { Point } from "./Point";
import { PointDefinition, Zone as ZoneType } from "protos/farm/farm";
import { titleCase } from "common/utils/strings";
import { useCanEditFarmGeometry } from "portal/views/farms/permissions";
import { useDispatch } from "react-redux";
import { useFarmExplorer } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { Zone } from "./Zone";
import { ZoneBufferEditor } from "./ZoneBufferEditor";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FitZoneIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import React, {
  FC,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useState,
} from "react";
import SaveIcon from "@mui/icons-material/Save";

interface DetailsPanelProps {
  updateFarm: UpdateFarm;
  /** fits the map to the selected zone */
  onFitZone?: () => void;
}

export const DetailsPanel: FunctionComponent<DetailsPanelProps> = ({
  updateFarm,
  onFitZone,
}) => {
  const { showDetails, selection, farm, boundaryEdit } = useFarmExplorer();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [confirmCloseOpen, setConfirmCloseOpen] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);

  const saveBoundaryEdit = async (): Promise<void> => {
    if (!farm || !boundaryEdit) {
      return;
    }
    const newZone = newZoneFromEdit(boundaryEdit);
    const patch = boundaryEdit.pending
      ? newZone && buildNewZonePatch(farm, newZone)
      : buildBoundaryEditPatch(farm, boundaryEdit);
    if (!patch) {
      return;
    }
    setSaving(true);
    try {
      if (await updateFarm(patch)) {
        const createdZone = newZone && patch.zones?.[0];
        // the stored zone replaces this one once the farm is refetched
        dispatch(
          createdZone
            ? setSelection({
                type: "zone",
                selection: ZoneType.fromPartial(createdZone),
              })
            : endBoundaryEdit()
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const canSaveBoundaryEdit =
    boundaryEdit &&
    (boundaryEdit.pending
      ? !boundaryEditProblem(boundaryEdit)
      : isBoundaryEditDirty(boundaryEdit) && boundaryEdit.points.length >= 3);

  const canEditFarm = useCanEditFarmGeometry();

  const updateZone = (zoneUpdate: DeepPartial<ZoneType>): void => {
    updateFarm({
      // NOTE: the patch logic of the patch farm API handles a missing "name"
      // field as a request to set the farm name to the empty string.
      id: farm?.id,
      version: farm?.version,
      name: farm?.name,
      zones: [zoneUpdate],
    });
  };

  const updatePoint = (pointUpdate: DeepPartial<PointDefinition>): void => {
    updateFarm({
      // NOTE: the patch logic of the patch farm API handles a missing "name"
      // field as a request to set the farm name to the empty string.
      id: farm?.id,
      version: farm?.version,
      name: farm?.name,
      pointDefs: [pointUpdate],
    });
  };
  const getDetailComponent = (): ReactNode => {
    if (boundaryEdit?.pending) {
      return <NewZoneDetails pending={boundaryEdit.pending} />;
    }
    switch (selection?.type) {
      case "zone": {
        return (
          <DeleteWrapper
            updateZone={updateZone}
            entity={{ zone: selection.selection }}
          >
            <ZoneBufferEditor
              zone={selection.selection}
              updateZone={updateZone}
            />
            {selection.selection.contents?.field && (
              <Field zone={selection.selection} updateZone={updateZone} />
            )}
            {selection.selection.contents?.centerPivot && (
              <CenterPivot zone={selection.selection} updateZone={updateZone} />
            )}
            {selection.selection.contents?.obstacle && (
              <Obstacle zone={selection.selection} updateZone={updateZone} />
            )}
            {selection.selection.contents?.path && (
              <Path zone={selection.selection} />
            )}
            {!selection.selection.contents?.field &&
              !selection.selection.contents?.obstacle &&
              !selection.selection.contents?.path && (
                <Zone zone={selection.selection} updateZone={updateZone} />
              )}
          </DeleteWrapper>
        );
      }
      case "point": {
        return canEditFarm ? (
          <EditablePoint point={selection.selection} onSave={updatePoint} />
        ) : (
          <Point point={selection.selection} />
        );
      }
      default:
    }
  };
  const getSelectionName = (): ReactNode => {
    if (boundaryEdit?.pending) {
      return titleCase(
        t("utils.actions.newLong", {
          subject: t(NEW_ZONE_TYPE_I18N_KEYS[boundaryEdit.pending.kind]),
        })
      );
    }
    switch (selection?.type) {
      case "zone":
      case "point": {
        return selection.selection.name;
      }
      default:
    }
  };

  const close = (): void => {
    dispatch(setSelection(undefined));
    dispatch(setFocus(undefined));
  };

  return (
    showDetails && (
      <Paper className="flex flex-col gap-2 w-full h-full pt-2 overflow-y-auto">
        {confirmCloseOpen && (
          <ConfirmationDialog
            title={t("views.farms.editBoundary.discardTitle")}
            description={t("views.farms.editBoundary.discardDescription")}
            destructive
            yesText={t("utils.actions.discard")}
            onClose={() => setConfirmCloseOpen(false)}
            onYes={close}
          />
        )}
        <div className="flex justify-between px-2 gap-1">
          <Typography variant="h4" className="break-all">
            {getSelectionName()}
          </Typography>
          <div className="flex my-auto shrink-0">
            {onFitZone && selection?.type === "zone" && (
              <Tooltip
                title={t("utils.actions.goToLong", {
                  subject: selection.selection.name,
                })}
              >
                <IconButton color="info" onClick={onFitZone}>
                  <FitZoneIcon />
                </IconButton>
              </Tooltip>
            )}
            {boundaryEdit && (
              <Tooltip title={t("utils.actions.save")}>
                <span>
                  <IconButton
                    color="primary"
                    disabled={!canSaveBoundaryEdit || isSaving}
                    onClick={saveBoundaryEdit}
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton
              color="info"
              onClick={() => {
                if (boundaryEdit && isBoundaryEditDirty(boundaryEdit)) {
                  setConfirmCloseOpen(true);
                } else {
                  close();
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <Divider />
        {getDetailComponent()}
      </Paper>
    )
  );
};

interface DeleteWrapperProps {
  // to be extended with other entity types in the future
  entity: { zone?: ZoneType };
  updateZone?: (update: DeepPartial<ZoneType>) => void;
}

const DeleteWrapper: FC<PropsWithChildren<DeleteWrapperProps>> = ({
  children,
  entity,
  updateZone,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const canEditGeometry = useCanEditFarmGeometry();
  return (
    <div className="flex flex-col">
      {confirmOpen && (
        <ConfirmationDialog
          title={t("utils.actions.delete")}
          description={
            entity.zone?.name ?? titleCase(t("models.farms.zone_one"))
          }
          destructive
          onClose={() => setConfirmOpen(false)}
          onYes={() => {
            if (entity.zone) {
              updateZone?.({
                ...entity.zone,
                version: { ...entity.zone.version, deleted: true },
              });
            }
          }}
        />
      )}
      {children}
      {canEditGeometry && (
        <div className="flex flex-col mt-auto p-2">
          <Button
            color={"error"}
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("utils.actions.delete")}
          </Button>
        </div>
      )}
    </div>
  );
};
